import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Twitter, ArrowRight } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { getImageUrl } from "../../utils/image";

export default function Footer() {
  const { get } = useSettings();
  const year = new Date().getFullYear();

  let companyLinks = [
    { label: "About Us", path: "/about" },
    { label: "Our Services", path: "/services" },
    { label: "Products", path: "/products" },
    { label: "Gallery", path: "/gallery" },
  ];
  let infoLinks = [
    { label: "Blog", path: "/blog" },
    { label: "Contact", path: "/contact" },
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms & Conditions", path: "/terms" },
  ];

  const rawCompany = get("footer_company_menu_json");
  const rawInfo = get("footer_info_menu_json");

  if (rawCompany) { try { companyLinks = JSON.parse(rawCompany); } catch {} }
  if (rawInfo) { try { infoLinks = JSON.parse(rawInfo); } catch {} }

  const copyrightTemplate = get("copyright_text") || "© {year} {site_name}. All rights reserved.";
  const copyrightText = copyrightTemplate
    .replace("{year}", year)
    .replace("{site_name}", get("site_name") || "Sava Fresh");

  const devName = get("copyright_dev_name");
  const devLink = get("copyright_dev_link");
  const devLogo = get("copyright_logo");

  let copyrightLinks = [
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms & Conditions", path: "/terms" },
  ];
  const rawCopyrightLinks = get("copyright_links_menu_json");
  if (rawCopyrightLinks) { try { copyrightLinks = JSON.parse(rawCopyrightLinks); } catch {} }

  const socials = [
    { Icon: Facebook, url: get("social_facebook") },
    { Icon: Instagram, url: get("social_instagram") },
    { Icon: Linkedin, url: get("social_linkedin") },
    { Icon: Twitter, url: get("social_twitter") },
  ].filter(s => s.url);

  const footerLogo = get("site_logo_dark") || "/logo-dark.png";
  const ctaHeading = get("footer_cta_heading") || "Ready to source globally?";
  const ctaSub = get("footer_cta_subheading") || "Contact us for competitive pricing and reliable supply chains.";
  const ctaBtnText = get("footer_cta_btn_text") || "Send Enquiry";
  const ctaBtnLink = get("footer_cta_btn_link") || "/contact";

  return (
    <footer className="bg-brand-primary text-white">
      {/* CTA Strip */}
      <div className="bg-brand-dark py-10">
        <div className="container-custom flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl font-bold mb-1">{ctaHeading}</h3>
            <p className="text-brand-muted text-sm">{ctaSub}</p>
          </div>
          <Link to={ctaBtnLink}
            className="bg-white text-brand-primary px-8 py-3 rounded-xl font-semibold text-sm hover:bg-brand-pale transition-all duration-300 flex items-center gap-2 shadow-soft whitespace-nowrap">
            {ctaBtnText} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-5">
              <img src={getImageUrl(footerLogo)} alt={get("site_name") || "Sava Fresh"} className="h-14 w-auto object-contain" />
            </div>
            <p className="text-brand-muted text-sm leading-relaxed mb-6">
              {get("footer_text") || "Connecting the world's finest food producers with global markets. Premium quality, trusted sourcing."}
            </p>
            {socials.length > 0 && (
              <div className="flex items-center gap-3">
                {socials.map(({ Icon, url }, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors duration-300">
                    <Icon className="w-4 h-4 text-white" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.path} className="text-brand-muted hover:text-white text-sm transition-colors duration-200 flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all duration-200" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Info Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider">Information</h4>
            <ul className="space-y-3">
              {infoLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.path} className="text-brand-muted hover:text-white text-sm transition-colors duration-200 flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all duration-200" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-accent-gold mt-0.5 flex-shrink-0" />
                <span className="text-brand-muted text-sm leading-relaxed">{get("address")}</span>
              </li>
              <li>
                <a href={`tel:${get("call_number")}`}
                  className="flex items-center gap-3 text-brand-muted hover:text-white text-sm transition-colors">
                  <Phone className="w-4 h-4 text-accent-gold" />
                  {get("call_number")}
                </a>
              </li>
              <li>
                <a href={`mailto:${get("email")}`}
                  className="flex items-center gap-3 text-brand-muted hover:text-white text-sm transition-colors">
                  <Mail className="w-4 h-4 text-accent-gold" />
                  {get("email")}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-5">
        <div className="container-custom flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-brand-muted text-xs">
            <span>{copyrightText}</span>
            {devName && (
              <span className="flex items-center gap-2 border-l border-white/10 pl-4">
                {devLogo && <img src={getImageUrl(devLogo)} alt="" className="h-4 w-auto object-contain" />}
                {devLink ? (
                  <a href={devLink} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    {devName}
                  </a>
                ) : (
                  <span>{devName}</span>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {copyrightLinks.map((link, i) => {
              const isExternal = link.path?.startsWith("http");
              if (isExternal) {
                return (
                  <a key={i} href={link.path} target="_blank" rel="noopener noreferrer" className="text-brand-muted hover:text-white text-xs transition-colors">
                    {link.label}
                  </a>
                );
              }
              return (
                <Link key={i} to={link.path} className="text-brand-muted hover:text-white text-xs transition-colors">
                  {link.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
